import { isToday } from '@/utils/Utils';
import { AdvancedFilterGroupNode, compileAdvancedFilter } from '@/domain/advancedFilter';
import { Egenskapsfilter, ValidEgenskapsfilter } from './Egenskapsfilter';
import { OmraderQueries } from './OmraderQueries';
import { OperatorE } from './Operator';

import { format } from 'date-fns';

export type filter = Egenskapsfilter | OmraderQueries;
export type inkluderBar =
    | 'metadata'
    | 'egenskaper'
    | 'relasjoner'
    | 'lokasjon'
    | 'vegsegmenter'
    | 'geometri'
    | 'alle'
    | 'antall'
    | 'lengde';

export type inkluder_egenskaperBar =
    'basis' | 'assosiasjon' | 'stedfesting' | 'geometri' | 'alle';

export class VegobjekterQueries {
    constructor(
        readonly segmentering: boolean | null = null,
        readonly egenskaper: Egenskapsfilter[] = [], // filters added as filters in UI
        readonly advancedFilter: AdvancedFilterGroupNode | null = null,
        readonly kategorifilter: Egenskapsfilter[] = [], // filters used for categorization
        readonly inkluder: inkluderBar[] = [],
        readonly inkluder_egenskaper: inkluder_egenskaperBar[] = [],
        readonly tidspunkt: string | null = new Date().toISOString().split('T')[0],
        readonly absoluteIntervals: boolean = false
    ) {}

    override(modifyObject: {
        [P in keyof VegobjekterQueries]?: VegobjekterQueries[P];
    }): VegobjekterQueries {
        return Object.assign(Object.create(VegobjekterQueries.prototype), {
            ...this,
            ...modifyObject,
        });
    }

    withSegmentering(segmentering: boolean): VegobjekterQueries {
        return this.override({ segmentering });
    }

    withTidspunkt(timestamp: Date | null): VegobjekterQueries {
        if (timestamp) {
            const tidspunkt = format(timestamp, 'yyyy-MM-dd');
            return this.override({ tidspunkt });
        } else {
            return this.override({ tidspunkt: null });
        }
    }

    withEgenskaper(egenskaper: Egenskapsfilter[]): VegobjekterQueries {
        return this.override({ egenskaper });
    }

    withAdvancedFilter(
        advancedFilter: AdvancedFilterGroupNode | null
    ): VegobjekterQueries {
        return this.override({ advancedFilter });
    }

    withKategorifilter(egenskapsfilter: Egenskapsfilter[]): VegobjekterQueries {
        const kategorifilter = this.kategorifilter.concat(egenskapsfilter);
        return this.override({ kategorifilter });
    }

    withInkluder(inkluder: inkluderBar[]): VegobjekterQueries {
        return this.override({ inkluder });
    }

    withInkluderEgenskaper(
        inkluder_egenskaper: inkluder_egenskaperBar[]
    ): VegobjekterQueries {
        return this.override({ inkluder_egenskaper });
    }

    withAbsoluteIntervals(absoluteIntervals: boolean): VegobjekterQueries {
        return this.override({ absoluteIntervals });
    }

    toParams() {
        const params: Record<string, string> = {};

        if (this.segmentering !== null) {
            params.segmentering = String(this.segmentering);
        }

        if (!!this.tidspunkt && !isToday(new Date(this.tidspunkt))) {
            params.tidspunkt = this.tidspunkt;
        }

        if (
            this.egenskaper.length > 0 ||
            this.kategorifilter.length > 0 ||
            this.advancedFilter
        ) {
            const compiledBaseExpression = this.advancedFilter
                ? compileAdvancedFilter(this.advancedFilter)
                : this.filtersToExpression(this.egenskaper);
            const compiledCategoryExpression = this.categoryFiltersToExpression();
            const expressions = [
                compiledBaseExpression,
                compiledCategoryExpression,
            ].filter((expression): expression is string => !!expression);

            if (expressions.length === 1) {
                params.egenskap = expressions[0];
            } else if (expressions.length > 1) {
                params.egenskap = expressions
                    .map(expression => `(${expression})`)
                    .join(' AND ');
            }
        }

        if (this.inkluder.length > 0) {
            params.inkluder = this.inkluder.join(',');
        }

        if (this.inkluder_egenskaper.length > 0) {
            params.inkluder_egenskaper = this.inkluder_egenskaper.join(',');
        }

        return params;
    }
    static invertOperator(op: OperatorE): OperatorE {
        switch (op) {
            case OperatorE.BEQ:
                return OperatorE.LEQ;
            case OperatorE.LEQ:
                return OperatorE.BEQ;
            case OperatorE.LT:
                return OperatorE.GT;
            case OperatorE.GT:
                return OperatorE.LT;
            default:
                throw new Error('Not supported for inversion');
        }
    }
    invert(filter: Egenskapsfilter): Egenskapsfilter {
        if (filter.operator === null || typeof filter.verdi !== 'number') {
            throw new Error('Kan bare invertere numeriske egenskapsfilter');
        }
        return new Egenskapsfilter(
            filter.egenskapstype,
            VegobjekterQueries.invertOperator(filter.operator),
            -filter.verdi
        );
    }
    /**
     *  Merges two maps
     *  If matching keys are found, their values are joined using a specified delimiter
     */
    merge<T>(a: Map<T, string>, b: Map<T, string>, delimiter: string): Map<T, string> {
        const keys = new Set([...a.keys(), ...b.keys()]);
        const resultMap = new Map();
        for (const key of keys) {
            resultMap.set(key, [a.get(key), b.get(key)].filter(v => v).join(delimiter));
        }
        return resultMap;
    }

    filterToMap(filters: Egenskapsfilter[]): Map<number, ValidEgenskapsfilter[]> {
        return (
            filters
                .filter(egenskapsfilter => egenskapsfilter.isValid())
                // Group by egenskapstype.id
                .reduce((prev, current) => {
                    const currentEgenskapstypeList =
                        prev.get(current.egenskapstype.id) || [];
                    prev.set(
                        current.egenskapstype.id,
                        currentEgenskapstypeList.concat(current)
                    );
                    return prev;
                }, new Map<number, ValidEgenskapsfilter[]>())
        );
    }

    allFiltersToParam(allFilters: Egenskapsfilter[]): Map<number, string> {
        const filterGroups = this.filterToMap(allFilters);
        const result = new Map<number, string>();

        for (const [id, values] of filterGroups.entries()) {
            const operator = values[0].egenskapstype.erEnum() ? 'OR' : 'AND';
            const formattedValues = values.join(` ${operator} `);
            result.set(id, `(${formattedValues})`);
        }

        return result;
    }

    private filtersToExpression(filters: Egenskapsfilter[]): string | null {
        const expression = [...this.allFiltersToParam(filters).values()].join(' AND ');
        return expression.length > 0 ? expression : null;
    }

    private categoryFiltersToExpression(): string | null {
        if (this.kategorifilter.length === 0) {
            return null;
        }

        const kategorifilter = this.allFiltersToParam(this.kategorifilter);

        if (
            this.absoluteIntervals &&
            this.kategorifilter.length > 0 &&
            this.kategorifilter[0].egenskapstype.erNummerisk()
        ) {
            kategorifilter.clear();
            const originalFilterMap = this.filterToMap(this.kategorifilter);
            const [key, filters] = Array.from(originalFilterMap.entries())[0];
            if (
                this.kategorifilter.length == 1 &&
                this.kategorifilter[0].operator == OperatorE.LEQ
            ) {
                const newFilter = [filters[0], this.invert(filters[0])];
                kategorifilter.set(key, '(' + newFilter.join(' AND ') + ')');
            } else if (
                this.kategorifilter.length == 2 &&
                this.kategorifilter[0].operator === OperatorE.GT &&
                this.kategorifilter[1].operator === OperatorE.LEQ
            ) {
                kategorifilter.set(
                    key,
                    `((${filters[0]} AND ${filters[1]}) OR (${this.invert(filters[0])} AND ${this.invert(
                        filters[1]
                    )}))`
                );
            } else if (
                this.kategorifilter.length == 1 &&
                this.kategorifilter[0].operator == OperatorE.GT
            ) {
                const newFilter = [filters[0], this.invert(filters[0])];
                kategorifilter.set(key, '(' + newFilter.join(' OR ') + ')');
            }
        }

        const expression = [...kategorifilter.values()].join(' AND ');
        return expression.length > 0 ? expression : null;
    }
}
